/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectTestCaseDialogMain.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, CardContent, Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomSearchbarWithApiV2 from "../../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import { vlcCardStyles } from "../../../../components/customComponentsV2/vlc/config/vlcConfig";
import { useSelector } from "react-redux";
import VlcCardContent from "../../../../components/customComponentsV2/vlc/cards/VlcCardContent";
import { componentStyle } from "../../../../config/appTheme";
import CustomPaginationV2 from "../../../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import testCasesActions from "../../../../redux/actions/testCasesActions";
import CustomCheckBoxWithoutFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import CustomToolTip from "../../../../components/infoButton/CustomToolTip";
import CustomCheckBoxWithFormControl from "../../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithFormControl";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const normalizeToList = (val) => {
  if (Array.isArray(val)) return val;
  if (val) return [val];
  return [];
};

const extractCompKeywords = (comp, keywordList, seen) => {
  if (!comp || !Array.isArray(comp.keywords)) return;
  for (const kw of comp.keywords) {
    if (!kw) continue;
    const id = typeof kw === "object" ? (kw.id ?? kw.value) : kw;
    if (id === undefined || id === null || id === "") continue;
    const seenKey = String(id);
    if (!seen.has(seenKey)) {
      seen.add(seenKey);
      keywordList.push(id);
    }
  }
};

const getUnlinkedTestCaseTooltip = (testCase) => {
  const missingLinks = [];

  if (testCase?.is_component_linked === false) {
    missingLinks.push("component");
  }
  if (testCase?.is_lr_linked === false) {
    missingLinks.push(
      missingLinks.length === 0 &&
        testCase?.is_component_linked !== false &&
        testCase?.is_campaign_linked !== false
        ? "legal requirements"
        : "legal requirement",
    );
  }
  if (testCase?.is_campaign_linked === false) {
    missingLinks.push("campaign");
  }

  return missingLinks.length
    ? `Test case not linked to any ${missingLinks.join(", ")}`
    : "";
};

function SelectTestCaseDialogMain({
  open,
  selectedTestCase,
  setSelectedTestCase,
  selectedTestCaseIds,
  setSelectedTestCaseIds,
  selectedLegalMain,
  lrID,
  entityType,
  entityID,
  selectedEEMain,
  selectedSWMMain,
  selectedVSMain,
}) {
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const [testCase, setTestCase] = useState([]);
  const [totalTestCase, setTotalTestCase] = useState();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  const getLRIds = () => {
    let lrList = [];
    if (Array.isArray(selectedLegalMain)) {
      lrList = selectedLegalMain;
    } else if (selectedLegalMain) {
      lrList = [selectedLegalMain];
    }

    const lrIds = [];
    const seen = new Set();

    for (const item of lrList) {
      const id = item?.lr_id ?? item?.id;
      if (id !== undefined && id !== null && id !== "") {
        const seenKey = String(id);
        if (!seen.has(seenKey)) {
          seen.add(seenKey);
          lrIds.push(id);
        }
      }
    }

    if (lrIds.length === 0 && lrID !== undefined && lrID !== null && lrID !== "") {
      lrIds.push(lrID);
    }

    return lrIds;
  };

  const getKeywords = () => {
    const eeList = normalizeToList(selectedEEMain);
    const swmList = normalizeToList(selectedSWMMain);
    const vsList = normalizeToList(selectedVSMain);

    const allComponents = [...eeList, ...swmList, ...vsList];
    for (const vsItem of vsList) {
      if (Array.isArray(vsItem?.vcData)) {
        allComponents.push(...vsItem.vcData);
      }
    }

    const keywordList = [];
    const seen = new Set();

    for (const comp of allComponents) {
      extractCompKeywords(comp, keywordList, seen);
    }

    return keywordList;
  };

  const enableSelectAll =
    (testCase || []).length > 0 &&
    (testCase || []).every((item) =>
      (selectedTestCaseIds || [])
        .map(String)
        .includes(String(item.test_case_id)),
    );

  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedTestCaseIdSet = new Set(
      (selectedTestCaseIds || []).map(String),
    );
    const syncedSelectedTestCases = (testCase || []).filter((item) =>
      selectedTestCaseIdSet.has(String(item.test_case_id)),
    );

    setSelectedTestCase(syncedSelectedTestCases);
  }, [open, selectedTestCaseIds, setSelectedTestCase, testCase]);

  return (
    <Box>
      <Grid container>
        <Grid
          item
          sm={12}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <CustomCheckBoxWithFormControl
            label="Select All"
            value={enableSelectAll}
            disabled={(testCase || []).length === 0}
            onChange={(e) => {
              const isChecked = e.target.checked;

              setSelectedTestCase(isChecked ? (testCase || []) : []);
              setSelectedTestCaseIds(
                isChecked ? (testCase || []).map((item) => item.test_case_id) : [],
              );
            }}
          />
        </Grid>
        <Grid item sm={12}>
          <CustomSearchbarWithApiV2
            apiAction={testCasesActions.get_test_cases_for_create_intersection}
            searchKey={"name"}
            placeholder={"Search Test Case"}
            getDataOnSearch={(data, resp) => {
              setTestCase(data || []);
              setTotalTestCase(resp?.total_count);
            }}
            params={{
              project_id: selectedProjectDetails?.project_id,
              variant_id: selectedVariantDetails?.variant_id,
              entity_type: entityType,
              lr_id: getLRIds(),
              keywords: getKeywords(),
              is_active_campaign: true,
              page_size: pageSize,
              page_number: page,
            }}
            iconColor={"black"}
          />
        </Grid>
        <Grid
          item
          sm={12}
          component={Paper}
          elevation={2}
          sx={{ borderRadius: 0 }}
        >
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              overflowY: "auto",
              p: 0,
              m: 0,
              height: "265px",
            }}
          >
            {testCase && testCase?.length > 0 ? (
              testCase.map((item) => {
                const unlinkedTooltip = getUnlinkedTestCaseTooltip(item);

                return (
                  <VlcCardContent
                    onClick={(selectedData) => { }}
                    data={item}
                    key={item?.["test_case_id"]}
                    isSelected={
                      (selectedTestCaseIds || [])
                        .map(String)
                        .includes(String(item?.test_case_id))
                    }
                    cardStyle={{
                      border: "none",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          width: "10%",
                        }}
                      >
                        <CustomCheckBoxWithoutFormControl
                          value={
                            (selectedTestCaseIds || [])
                              .map(String)
                              .includes(
                                String(item.test_case_id),
                              ) || false
                          }
                          onChange={(e) => {
                            const currentSelection = selectedTestCase || [];
                            const newSelectedTestCase = e.target.checked
                              ? [...currentSelection, item]
                              : currentSelection.filter(
                                (selectedItem) =>
                                  selectedItem.test_case_id !==
                                  item.test_case_id,
                              );
                            let newTestCaseIds = newSelectedTestCase.map(
                              (item) => item.test_case_id,
                            );
                            setSelectedTestCaseIds(newTestCaseIds);
                            setSelectedTestCase(newSelectedTestCase);
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          width: "70%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            p: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >{`Test Case ID: `}</Box>
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                              }}
                            >
                              <CustomToolTip length={20}>
                                {item.manual_test_case_id}
                              </CustomToolTip>
                            </Box>
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >{`Test Case Title: `}</Box>
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                              }}
                            >
                              <CustomToolTip length={20}>
                                {item.name}
                              </CustomToolTip>
                            </Box>
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                                fontWeight: 600,
                              }}
                            >{`Description: `}</Box>
                            <Box
                              sx={{
                                ...componentStyle.font.text,
                              }}
                            >
                              <CustomToolTip length={20}>
                                {item.description}
                              </CustomToolTip>
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          width: "20%",
                          display: "flex",
                          justifyContent: "center",
                          pt: 1,
                        }}
                      >
                        {unlinkedTooltip && (
                          <CustomToolTip
                            type="component"
                            title={unlinkedTooltip}
                          >
                            <ErrorOutlineIcon
                              sx={{ color: "black", fontSize: 20 }}
                            />
                          </CustomToolTip>
                        )}
                      </Box>
                    </Box>
                  </VlcCardContent>
                );
              })
            ) : (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  mt: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                No data available
              </Typography>
            )}
          </CardContent>
          <CustomPaginationV2
            page={page}
            rowsPerPage={pageSize}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            totalItems={totalTestCase}
          />
        </Grid>
        <Grid item sm={12}></Grid>
      </Grid>
    </Box>
  );
}

SelectTestCaseDialogMain.propTypes = {
  open: PropTypes.bool,
  selectedTestCase: PropTypes.array,
  setSelectedTestCase: PropTypes.func,
  selectedTestCaseIds: PropTypes.array,
  setSelectedTestCaseIds: PropTypes.func,
  selectedLegalMain: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  lrID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
  entityID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedEEMain: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  selectedSWMMain: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  selectedVSMain: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default SelectTestCaseDialogMain;
